import { useMemo, useState, useCallback } from "react"
import { toast } from "react-toastify"
import { Col, Row } from "reactstrap"
import TableContainer from "components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import VehicleFuelStockAddEdit from "./VehicleFuelStockAddEdit"
import { VehicleFuelStockListHeader } from "./VehicleFuelStockListHeader"

import {
  useVehicleFuelStocks,
  useDeleteVehicleFuelStock,
} from "hooks/useVehicleFuelStocks"
import { useListParams } from "hooks/useListParams"
import { useFilteredVehicleOptions } from "hooks/stockFilterHook"
import PageWrapper from "components/Common/PageWrapper"
import { formatReadableDate } from "utils/formatDate"
import ActionButtonsCell from "components/Common/ActionButtonsCell"
import FuelPurchaseSummary from "./FuelPurchaseSummary"
import FuelPurchaseInfoCell from "components/Common/VehicleFuel/FuelPurchaseInfoCell"

const VehicleFuelStockList = () => {
  document.title = "Purchased Fuel List"

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [fuelStock, setFuelStock] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  const {
    params,
    setPage,
    setSearch,
    resetParams,
    updateParams,
    hasActiveFilters,
  } = useListParams({
    page: 1,
    search: "",
    vehicle_id: "",
    fuel_type_id: "",
    fuel_station_id: "",
    date_from: "",
    date_to: "",
  })

  const queryParams = useMemo(
    () => ({
      page: params.page ?? 1,
      per_page: params.per_page ?? 10,
      search: params.search,
      vehicle_id: params.vehicle_id,
      fuel_type_id: params.fuel_type_id,
      fuel_station_id: params.fuel_station_id,
      date_from: params.date_from,
      date_to: params.date_to,
    }),
    [params]
  )

  const {
    data: fuelStocksResponse,
    isLoading,
    refetch: refetchFuelStocks,
    isRefetching,
  } = useVehicleFuelStocks(queryParams)
  const { mutateAsync: deleteFuelStock, isPending: deleteLoading } =
    useDeleteVehicleFuelStock()
  const { isLoading: optionLoading, raw: rawData } = useFilteredVehicleOptions()

  const locationsData = useMemo(
    () => rawData?.locations ?? [],
    [rawData?.locations]
  )
  const employeesData = useMemo(
    () => rawData?.employees ?? [],
    [rawData?.employees]
  )
  const fuelTypesData = useMemo(
    () => rawData?.fuel_types ?? [],
    [rawData?.fuel_types]
  )

  const toggleModal = useCallback(() => {
    if (modal) {
      setModal(false)
      setFuelStock(null)
      setIsEdit(false)
    } else setModal(true)
  }, [modal])

  const handleAddNewClick = useCallback(() => {
    setFuelStock(null)
    setIsEdit(false)
    toggleModal()
  }, [toggleModal])

  const handleEditClick = useCallback(
    item => {
      setFuelStock(item)
      setIsEdit(true)
      toggleModal()
    },
    [toggleModal]
  )

  const handleDeleteClick = useCallback(item => {
    setFuelStock(item)
    setDeleteModal(true)
  }, [])

  const handleDelete = useCallback(async () => {
    if (!fuelStock?.id) return
    try {
      const res = await deleteFuelStock(fuelStock.id)
      toast.success(res?.message ?? "Purchased fuel deleted successfully.")
      setDeleteModal(false)
    } catch (error) {
      console.error(error)
      toast.error("Failed to delete fuel stock.")
    }
  }, [fuelStock, deleteFuelStock])

  const handleRefresh = () => refetchFuelStocks()

  const columns = useMemo(
    () => [
      {
        header: "#",
        accessorKey: "id",
      },
      {
        header: "Fuel Purchase Info",
        accessorKey: "fuel_info",
        cell: ({ row }) => <div className="text-wrap">
          <FuelPurchaseInfoCell data={row.original} />
        </div>,
      },
      {
        header: "Dates",
        accessorKey: "dates",
        cell: ({ row }) =>
          row.original.created_at
            ? formatReadableDate(row.original.created_at)
            : "-",
      },
      {
        header: "Action",
        cell: ({ row }) => {
          const data = row.original
          const buttons = [
            {
              type: "edit",
              btnId: `edit-${data.stock_id}`,
              btnName: "Edit Stock",
              onClick: () => handleEditClick(data),
            },
            {
              type: "delete",
              btnId: `delete-${data.id}`,
              btnName: "Delete Stock",
              onClick: () => handleDeleteClick(data),
            },
          ]

          return <ActionButtonsCell buttons={buttons} />
        },
      },
    ],
    [handleEditClick, handleDeleteClick]
  )

  const data = fuelStocksResponse?.data?.data?.data || []
  const paginationInfo = fuelStocksResponse?.data?.data
  const totals = fuelStocksResponse?.data?.totals
  const summary = fuelStocksResponse?.data?.summary
  const comparison_basis = fuelStocksResponse?.data?.periods?.comparison_basis

  return (
    <PageWrapper title="Purchased Fuel" breadcrumbItem="Purchased Fuel List">
      <VehicleFuelStockListHeader
        handleAddNewClick={handleAddNewClick}
        handleRefresh={handleRefresh}
        resetParams={resetParams}
        isRefetching={isRefetching}
        hasActiveFilters={hasActiveFilters}
        total={paginationInfo?.total ?? 0}
      />

      <div>
        <FuelPurchaseSummary totals={totals} summary={summary} comparison_basis={comparison_basis} />
      </div>

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        loading={deleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />

      <Row>
        <Col sm={4}>
          <GlobalInputSearch value={params.search} onChange={setSearch} />
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={data}
        isPagination
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isRefetching}
      />

      <VehicleFuelStockAddEdit
        modal={modal}
        isEdit={isEdit}
        fuelStock={fuelStock}
        toggleModal={toggleModal}
        fuelTypes={fuelTypesData}
        locations={locationsData}
        employees={employeesData}
        optionLoading={optionLoading}
      />
    </PageWrapper>
  )
}

export default VehicleFuelStockList
